import { browserName } from 'react-device-detect';

import { authenticatedLogger } from '../authenticatedLogger';
import { logger } from '../logger';

const MAX_ERRORS = 50;

function logCanonical(message: string, context: Record<string, unknown>): void {
  try {
    const katalLogger = authenticatedLogger.getLoggerSync();
    if (katalLogger) {
      katalLogger.info(message, context);
    } else {
      logger.info(message, context).catch(() => {});
    }
  } catch {
    // never throw
  }
}

interface SessionState {
  sessionId: string;
  userId?: string;
  userAlias?: string;
  marketplace?: string;
  placement?: string;
  deviceFamily?: string;
  browser: string;
  didTransform?: boolean;
  transformationCount?: number;
  transformationVariants?: string[];
  didDownload?: boolean;
  downloadCount?: number;
  didCreateCampaign?: boolean;
  campaignId?: string;
  errors?: string[];
}

class SessionContext {
  private static instance: SessionContext;
  private state: SessionState;
  private emitSequence = 0;
  private initTime = Date.now();

  private constructor() {
    this.state = {
      sessionId: crypto.randomUUID?.() ?? Math.random().toString(36).slice(2),
      browser: browserName || 'Unknown',
    };

    // Read persisted selections — each key independently guarded
    const safeRead = (key: string): string | undefined => {
      try {
        const raw = localStorage.getItem(key);
        return raw ? JSON.parse(raw)?.value : undefined;
      } catch {
        return undefined;
      }
    };
    this.state.marketplace = safeRead('selectedMarketplace');
    this.state.placement = safeRead('selectedPlacement');
    this.state.deviceFamily = safeRead('selectedDeviceFamily');
  }

  static getInstance(): SessionContext {
    if (!SessionContext.instance) {
      SessionContext.instance = new SessionContext();
    }
    return SessionContext.instance;
  }

  update(fields: Partial<Omit<SessionState, 'sessionId' | 'browser'>>): void {
    Object.assign(this.state, fields);
  }

  addTransformationVariant(variant: string): void {
    if (variant && !this.state.transformationVariants?.includes(variant)) {
      this.state.transformationVariants = [...(this.state.transformationVariants || []), variant];
    }
  }

  addError(error: string): void {
    this.state.errors = [...(this.state.errors || []), error].slice(-MAX_ERRORS);
  }

  emit(trigger: string): void {
    try {
      this.emitSequence++;
      const snapshot = {
        ...this.state,
        canonical: true,
        type: 'SESSION',
        emitTrigger: trigger,
        emitSequence: this.emitSequence,
        timestamp: new Date().toISOString(),
        sessionDurationMs: Date.now() - this.initTime,
      };
      if (trigger === 'pagehide') {
        const katalLogger = authenticatedLogger.getLoggerSync();
        if (katalLogger) {
          katalLogger.info('CanonicalLog', snapshot);
        }
      } else {
        logCanonical('CanonicalLog', snapshot);
      }
    } catch {
      // never throw
    }
  }

  getSessionId(): string {
    return this.state.sessionId;
  }

  getDownloadCount(): number {
    return this.state.downloadCount ?? 0;
  }
}

export const sessionContext = SessionContext.getInstance();
